import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, Card, Button, Empty, Input, Select, Label, Textarea } from "@/components/Primitives";
import { useAuth } from "@/context/AuthContext";
import { Plus, Mail, Pencil, Trash2, X, KeyRound, ShieldOff, ShieldCheck, Check } from "lucide-react";
import { toast } from "sonner";

const PRESET_LABELS = {
  full_staff: "Full staff",
  design: "Design",
  sales: "Sales",
  support: "Support",
  finance: "Finance",
  custom: "Custom",
};

const SCOPE_LABELS = {
  customers: "Customers",
  leads: "Leads",
  quotes: "Quotes",
  invoices: "Invoices",
  projects: "Projects",
  apps: "Applications",
  products: "Software",
  tickets: "Support tickets",
  team: "Team",
  roadmap: "Roadmap",
  revenue: "Revenue",
  settings: "Settings",
};

function gbp(v, c = "GBP") {
  const s = c === "GBP" ? "£" : c === "USD" ? "$" : c + " ";
  return `${s}${(v ?? 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
}

const EMPTY = {
  name: "", email: "", role_title: "", avatar_url: "",
  type: "internal", hourly_rate: 0, currency: "GBP",
  weekly_capacity_hours: 40, phone: "", notes: "",
  assigned_customer_ids: [], active: true,
};

export default function TeamPage() {
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [editing, setEditing] = useState(null); // null | "new" | <memberObj>
  const [grantFor, setGrantFor] = useState(null);
  const [permsFor, setPermsFor] = useState(null);
  const isOwner = user?.role === "admin" || user?.is_owner;

  const load = async () => {
    const [m, c] = await Promise.all([api.get("/team"), api.get("/customers")]);
    setMembers(m.data || []);
    setCustomers(c.data || []);
  };
  useEffect(() => { load(); }, []);

  const companyMap = Object.fromEntries(customers.map((c) => [c.id, c.company]));

  const onDelete = async (m) => {
    if (!window.confirm(`Remove ${m.name}?`)) return;
    await api.delete(`/team/${m.id}`);
    toast.success("Team member removed");
    load();
  };

  const revokeAccess = async (m) => {
    if (!window.confirm(`Revoke platform access for ${m.name}? They won't be able to log in anymore.`)) return;
    await api.post(`/team/${m.id}/revoke-access`);
    toast.success(`Access revoked for ${m.name}`);
  };

  const internalCount = members.filter((m) => (m.type || "internal") === "internal").length;
  const externalCount = members.filter((m) => m.type === "external").length;

  return (
    <div className="space-y-6">
      <PageHeader
        breadcrumb="Workspace · Team"
        title="Team"
        subtitle={`${members.length} member${members.length === 1 ? "" : "s"} · ${internalCount} internal · ${externalCount} external`}
        actions={<Button onClick={() => setEditing("new")} data-testid="add-staff-btn"><Plus className="w-4 h-4" strokeWidth={2}/>Add staff</Button>}
      />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {members.map((m) => {
          const isExt = m.type === "external";
          return (
            <div key={m.id} className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] p-5 hover:-translate-y-[1px] transition-all" data-testid="team-card">
              <div className="flex items-start gap-3">
                <img src={m.avatar_url || "/brand/soubor-one-mark.png"} alt="" onError={(e) => { e.currentTarget.src = "/brand/soubor-one-mark.png"; }} className="w-12 h-12 rounded-full object-cover ring-2 ring-[hsl(var(--border))]"/>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-semibold truncate">{m.name}</div>
                    <span className={`text-[10px] uppercase tracking-widest font-semibold px-1.5 py-0.5 rounded ${isExt ? "bg-amber-500/15 text-amber-600 dark:text-amber-400" : "bg-[hsl(var(--primary-muted))] text-[hsl(var(--primary))]"}`}>
                      {isExt ? "External" : "Internal"}
                    </span>
                  </div>
                  <div className="text-[12px] text-[hsl(var(--text-muted))] truncate">{m.role_title}</div>
                </div>
                <div className="flex items-center gap-1">
                  {isOwner && (
                    <button onClick={() => setGrantFor(m)} className="p-1.5 rounded-md hover:bg-[hsl(var(--surface-hover))] text-[hsl(var(--text-muted))] hover:text-[hsl(var(--primary))]" title="Grant platform access" data-testid="grant-access-btn"><KeyRound className="w-3.5 h-3.5" strokeWidth={1.6}/></button>
                  )}
                  {isOwner && (
                    <button onClick={() => setPermsFor(m)} className="p-1.5 rounded-md hover:bg-[hsl(var(--surface-hover))] text-[hsl(var(--text-muted))] hover:text-[hsl(var(--primary))]" title="Permissions" data-testid="permissions-btn"><ShieldCheck className="w-3.5 h-3.5" strokeWidth={1.6}/></button>
                  )}
                  <button onClick={() => setEditing(m)} className="p-1.5 rounded-md hover:bg-[hsl(var(--surface-hover))] text-[hsl(var(--text-muted))]" title="Edit"><Pencil className="w-3.5 h-3.5" strokeWidth={1.6}/></button>
                  {isOwner && (
                    <button onClick={() => onDelete(m)} className="p-1.5 rounded-md hover:bg-[hsl(var(--surface-hover))] text-[hsl(var(--text-muted))] hover:text-[hsl(var(--danger))]" title="Delete"><Trash2 className="w-3.5 h-3.5" strokeWidth={1.6}/></button>
                  )}
                </div>
              </div>

              <div className="mt-4 flex items-center gap-3 text-[12px] text-[hsl(var(--text-muted))]">
                <Mail className="w-3.5 h-3.5" strokeWidth={1.6}/>{m.email}
              </div>

              <div className="mt-4 grid grid-cols-2 gap-3 text-[11px]">
                <div>
                  <div className="uppercase tracking-widest text-[hsl(var(--text-muted))] font-semibold">Rate</div>
                  <div className="font-mono font-semibold text-[13px] mt-0.5">{m.hourly_rate > 0 ? `${gbp(m.hourly_rate, m.currency)}/hr` : "—"}</div>
                </div>
                <div>
                  <div className="uppercase tracking-widest text-[hsl(var(--text-muted))] font-semibold">Capacity</div>
                  <div className="font-mono font-semibold text-[13px] mt-0.5">{m.weekly_capacity_hours || 40}h / wk</div>
                </div>
              </div>

              {m.assigned_customer_ids?.length > 0 && (
                <div className="mt-4">
                  <div className="text-[10px] uppercase tracking-widest text-[hsl(var(--text-muted))] mb-2">Assigned customers</div>
                  <div className="flex flex-wrap gap-1.5">
                    {m.assigned_customer_ids.map((cid) => (
                      <span key={cid} className="text-[11px] font-semibold px-2 py-0.5 rounded-md bg-[hsl(var(--primary-muted))] text-[hsl(var(--primary))]">{companyMap[cid] || cid}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
        {members.length === 0 && <div className="col-span-full"><Empty title="No team yet" subtitle="Add your first staff member."/></div>}
      </div>

      {editing && (
        <StaffModal
          initial={editing === "new" ? EMPTY : editing}
          customers={customers}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}

      {grantFor && <GrantAccessModal member={grantFor} onClose={() => setGrantFor(null)} onDone={() => { setGrantFor(null); load(); }} onRevoke={() => { revokeAccess(grantFor); setGrantFor(null); }} />}
      {permsFor && <PermissionsModal member={permsFor} onClose={() => setPermsFor(null)} onSaved={() => { setPermsFor(null); load(); }} />}
    </div>
  );
}

function GrantAccessModal({ member, onClose, onDone, onRevoke }) {
  const [role, setRole] = useState("staff");
  const [password, setPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/team/${member.id}/grant-access`, { role, password });
      toast.success(`${member.name} can now log in as ${role}`);
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to grant access");
    } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm grid place-items-center p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="w-full max-w-md rounded-2xl bg-[hsl(var(--surface))] border border-[hsl(var(--border))] shadow-2xl" data-testid="grant-access-modal">
        <div className="px-6 py-4 border-b border-[hsl(var(--border))] flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold">Platform access</h3>
            <p className="text-[12px] text-[hsl(var(--text-muted))] mt-0.5">Give {member.name} a login to Soubor One.</p>
          </div>
          <button type="button" onClick={onClose} className="p-2 rounded-md hover:bg-[hsl(var(--surface-hover))]"><X className="w-4 h-4"/></button>
        </div>
        <div className="p-6 space-y-4">
          <div className="text-[12px] text-[hsl(var(--text-muted))] bg-[hsl(var(--background))] rounded-lg border border-[hsl(var(--border))] p-3">
            <div className="font-semibold text-[hsl(var(--text))] mb-1">What each role can do</div>
            <div className="space-y-1">
              <div><span className="font-semibold text-[hsl(var(--primary))]">Staff</span>. Read, create, edit. Cannot delete or manage team/tenant/Stripe.</div>
              <div><span className="font-semibold text-[hsl(var(--primary))]">Owner</span>. Full access, including deletes and team management.</div>
            </div>
          </div>
          <div><Label>Email</Label><Input value={member.email} disabled/></div>
          <div><Label>Role</Label>
            <Select value={role} onChange={(e) => setRole(e.target.value)} data-testid="grant-role-select">
              <option value="staff">Staff (recommended)</option>
              <option value="admin">Owner (full access)</option>
            </Select>
          </div>
          <div><Label>Temporary password *</Label><Input required type="password" minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Share this with them" data-testid="grant-password"/></div>
        </div>
        <div className="px-6 py-4 border-t border-[hsl(var(--border))] flex items-center justify-between gap-2">
          <Button type="button" variant="ghost" onClick={onRevoke} className="text-rose-600 hover:text-rose-700" data-testid="revoke-access-btn">
            <ShieldOff className="w-4 h-4" strokeWidth={1.6}/>Revoke access
          </Button>
          <div className="flex items-center gap-2">
            <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={saving || !password} data-testid="grant-confirm-btn">
              <KeyRound className="w-4 h-4" strokeWidth={2}/>{saving ? "Granting…" : "Grant access"}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}

function StaffModal({ initial, customers, onClose, onSaved }) {
  const [form, setForm] = useState({ ...EMPTY, ...initial });
  const [saving, setSaving] = useState(false);
  const isNew = !initial.id;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleCustomer = (cid) => {
    const curr = form.assigned_customer_ids || [];
    set("assigned_customer_ids", curr.includes(cid) ? curr.filter((x) => x !== cid) : [...curr, cid]);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        hourly_rate: parseFloat(form.hourly_rate) || 0,
        weekly_capacity_hours: parseInt(form.weekly_capacity_hours) || 40,
      };
      if (isNew) {
        await api.post("/team", payload);
        toast.success("Staff member added");
      } else {
        await api.patch(`/team/${initial.id}`, payload);
        toast.success("Staff member updated");
      }
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm grid place-items-center p-4" onClick={onClose}>
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="w-full max-w-2xl rounded-2xl bg-[hsl(var(--surface))] border border-[hsl(var(--border))] shadow-2xl max-h-[90vh] overflow-y-auto"
        data-testid="staff-modal"
      >
        <div className="px-6 py-4 border-b border-[hsl(var(--border))] flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold">{isNew ? "Add staff member" : `Edit ${initial.name}`}</h3>
            <p className="text-[12px] text-[hsl(var(--text-muted))] mt-0.5">Internal or external. Soubor One handles both.</p>
          </div>
          <button type="button" onClick={onClose} className="p-2 rounded-md hover:bg-[hsl(var(--surface-hover))]"><X className="w-4 h-4"/></button>
        </div>

        <div className="p-6 space-y-5">
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Name *</Label><Input required value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="staff-name"/></div>
            <div><Label>Email *</Label><Input required type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="staff-email"/></div>
            <div><Label>Role / Title *</Label><Input required value={form.role_title} onChange={(e) => set("role_title", e.target.value)} placeholder="e.g. Developer, Designer, Accountant"/></div>
            <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => set("phone", e.target.value)}/></div>
          </div>

          <div className="rounded-lg border border-[hsl(var(--border))] p-4 bg-[hsl(var(--background))]">
            <div className="text-[10px] uppercase tracking-widest text-[hsl(var(--text-muted))] font-semibold mb-3">Employment</div>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => set("type", "internal")}
                className={`rounded-lg border p-3 text-left transition-all ${form.type === "internal" ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary-muted))]" : "border-[hsl(var(--border))] hover:border-[hsl(var(--text-muted))]"}`}
                data-testid="staff-type-internal"
              >
                <div className="font-semibold text-[13px]">Internal</div>
                <div className="text-[11px] text-[hsl(var(--text-muted))] mt-0.5">On your payroll / in-house</div>
              </button>
              <button
                type="button"
                onClick={() => set("type", "external")}
                className={`rounded-lg border p-3 text-left transition-all ${form.type === "external" ? "border-amber-500 bg-amber-500/10" : "border-[hsl(var(--border))] hover:border-[hsl(var(--text-muted))]"}`}
                data-testid="staff-type-external"
              >
                <div className="font-semibold text-[13px]">External</div>
                <div className="text-[11px] text-[hsl(var(--text-muted))] mt-0.5">Contractor / freelancer / agency</div>
              </button>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label>Hourly rate</Label>
              <Input type="number" min="0" step="0.01" value={form.hourly_rate} onChange={(e) => set("hourly_rate", e.target.value)} data-testid="staff-rate"/>
            </div>
            <div>
              <Label>Currency</Label>
              <Select value={form.currency} onChange={(e) => set("currency", e.target.value)}>
                <option value="GBP">GBP</option>
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
              </Select>
            </div>
            <div>
              <Label>Weekly capacity (hrs)</Label>
              <Input type="number" min="0" max="168" step="1" value={form.weekly_capacity_hours} onChange={(e) => set("weekly_capacity_hours", e.target.value)} data-testid="staff-capacity"/>
            </div>
          </div>

          <div>
            <Label>Assigned customers</Label>
            <div className="flex flex-wrap gap-1.5 mt-1">
              {customers.map((c) => {
                const on = (form.assigned_customer_ids || []).includes(c.id);
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => toggleCustomer(c.id)}
                    className={`text-[11px] px-2 py-1 rounded-md border font-semibold transition-all ${on ? "bg-[hsl(var(--primary))] text-white border-[hsl(var(--primary))]" : "bg-[hsl(var(--surface))] border-[hsl(var(--border))] text-[hsl(var(--text-muted))] hover:border-[hsl(var(--primary))]"}`}
                  >
                    {c.company}
                  </button>
                );
              })}
              {customers.length === 0 && <span className="text-[11px] text-[hsl(var(--text-subtle))]">No customers yet</span>}
            </div>
          </div>

          <div><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Skills, notes, availability…"/></div>

          <div className="flex items-center gap-2">
            <input type="checkbox" id="active" checked={form.active} onChange={(e) => set("active", e.target.checked)}/>
            <label htmlFor="active" className="text-[13px]">Active</label>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-[hsl(var(--border))] flex items-center justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={saving} data-testid="staff-save-btn">{saving ? "Saving…" : isNew ? "Add staff" : "Save changes"}</Button>
        </div>
      </form>
    </div>
  );
}


function PermissionsModal({ member, onClose, onSaved }) {
  const [state, setState] = useState(null); // {has_access, role, permission_preset, permissions, effective_permissions}
  const [presets, setPresets] = useState({});
  const [scopes, setScopes] = useState([]);
  const [preset, setPreset] = useState("full_staff");
  const [perms, setPerms] = useState(new Set());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      const [cur, def] = await Promise.all([
        api.get(`/team/${member.id}/permissions`),
        api.get(`/permissions/presets`),
      ]);
      setState(cur.data);
      setPresets(def.data.presets || {});
      setScopes(def.data.scopes || []);
      const p = cur.data.permission_preset || "full_staff";
      setPreset(p);
      const effective = cur.data.effective_permissions || [];
      // If wildcard, treat as full
      if (effective.includes("*")) {
        setPerms(new Set(def.data.scopes.flatMap((s) => [`${s}:read`, `${s}:write`])));
      } else {
        setPerms(new Set(effective));
      }
    })().catch((e) => toast.error(e?.response?.data?.detail || "Failed to load permissions"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [member.id]);

  const applyPreset = (key) => {
    setPreset(key);
    if (key === "custom") return; // don't touch boxes
    const list = presets[key] || [];
    setPerms(new Set(list));
  };

  const toggle = (p) => {
    setPerms((s) => {
      const next = new Set(s);
      if (next.has(p)) next.delete(p); else next.add(p);
      return next;
    });
    setPreset("custom");
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/team/${member.id}/permissions`, {
        permission_preset: preset,
        permissions: preset === "custom" ? Array.from(perms) : null,
      });
      toast.success(`Permissions updated for ${member.name}`);
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save permissions");
    } finally { setSaving(false); }
  };

  if (!state) {
    return (
      <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" onClick={onClose}>
        <div className="w-[min(500px,100%)] bg-[hsl(var(--surface))] rounded-2xl p-8 text-center text-[13px] text-[hsl(var(--text-muted))]">Loading…</div>
      </div>
    );
  }

  if (!state.has_access) {
    return (
      <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" onClick={onClose}>
        <div onClick={(e) => e.stopPropagation()} className="w-[min(500px,100%)] bg-[hsl(var(--surface))] rounded-2xl border border-[hsl(var(--border))] p-6 text-center space-y-4">
          <ShieldOff className="w-10 h-10 mx-auto text-[hsl(var(--text-muted))]" strokeWidth={1.4}/>
          <div>
            <h3 className="text-lg font-semibold">No login yet</h3>
            <p className="text-[13px] text-[hsl(var(--text-muted))] mt-1">Grant platform access to {member.name} first, then come back to set permissions.</p>
          </div>
          <Button onClick={onClose}>Got it</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-2xl max-h-[92vh] overflow-y-auto bg-[hsl(var(--surface))] rounded-2xl border border-[hsl(var(--border))] shadow-2xl" data-testid="permissions-modal">
        <div className="px-6 py-4 border-b border-[hsl(var(--border))] flex items-center justify-between sticky top-0 bg-[hsl(var(--surface))] z-10">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-[hsl(var(--text-muted))] font-semibold inline-flex items-center gap-1.5"><ShieldCheck className="w-3 h-3" strokeWidth={2}/>Permissions</div>
            <h3 className="text-lg font-semibold mt-0.5">{member.name}</h3>
            <div className="text-[11px] text-[hsl(var(--text-muted))]">{member.role_title || "Staff"} · {member.email}</div>
          </div>
          <button type="button" onClick={onClose} className="p-2 rounded-md hover:bg-[hsl(var(--surface-hover))]"><X className="w-4 h-4"/></button>
        </div>

        <div className="p-6 space-y-5">
          {/* Preset picker */}
          <div>
            <Label>Preset</Label>
            <div className="mt-1 grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="preset-picker">
              {Object.keys(PRESET_LABELS).map((k) => {
                const active = preset === k;
                return (
                  <button
                    key={k}
                    type="button"
                    onClick={() => applyPreset(k)}
                    className={`text-left p-3 rounded-lg border transition-all ${active ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary-muted))]" : "border-[hsl(var(--border))] hover:border-[hsl(var(--text-muted))]"}`}
                    data-testid={`preset-${k}`}
                  >
                    <div className={`text-[12.5px] font-semibold ${active ? "text-[hsl(var(--primary))]" : ""}`}>{PRESET_LABELS[k]}</div>
                    <div className="text-[10.5px] text-[hsl(var(--text-muted))] mt-0.5 leading-snug">
                      {k === "full_staff" && "All internal areas except settings"}
                      {k === "design" && "Projects + apps, read customers & tickets"}
                      {k === "sales" && "Leads, quotes, customers, read tickets"}
                      {k === "support" && "Tickets + customer context"}
                      {k === "finance" && "Invoices, quotes, revenue"}
                      {k === "custom" && "Fine-tune with the tickboxes below"}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Per-scope tickbox grid */}
          <div className="rounded-xl border border-[hsl(var(--border))] overflow-hidden">
            <div className="grid grid-cols-[1fr_auto_auto] gap-4 px-4 py-2.5 bg-[hsl(var(--background))] border-b border-[hsl(var(--border))]">
              <div className="text-[10px] uppercase tracking-widest text-[hsl(var(--text-muted))] font-semibold">Section</div>
              <div className="text-[10px] uppercase tracking-widest text-[hsl(var(--text-muted))] font-semibold w-14 text-center">Read</div>
              <div className="text-[10px] uppercase tracking-widest text-[hsl(var(--text-muted))] font-semibold w-14 text-center">Write</div>
            </div>
            <div className="divide-y divide-[hsl(var(--border))]">
              {scopes.filter((s) => s !== "settings" && s !== "team").map((scope) => {
                const r = `${scope}:read`;
                const w = `${scope}:write`;
                return (
                  <div key={scope} className="grid grid-cols-[1fr_auto_auto] gap-4 px-4 py-2.5 items-center hover:bg-[hsl(var(--surface-hover))]">
                    <div className="text-[13px] font-medium">{SCOPE_LABELS[scope] || scope}</div>
                    <label className="w-14 flex justify-center cursor-pointer">
                      <input type="checkbox" checked={perms.has(r)} onChange={() => toggle(r)} className="w-4 h-4 accent-[hsl(var(--primary))]" data-testid={`perm-${scope}-read`}/>
                    </label>
                    <label className="w-14 flex justify-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={perms.has(w)}
                        onChange={() => {
                          // Write implies read. If ticking write, also tick read.
                          setPerms((s) => {
                            const n = new Set(s);
                            if (n.has(w)) {
                              n.delete(w);
                            } else {
                              n.add(w); n.add(r);
                            }
                            return n;
                          });
                          setPreset("custom");
                        }}
                        className="w-4 h-4 accent-[hsl(var(--primary))]"
                        data-testid={`perm-${scope}-write`}
                      />
                    </label>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="text-[11.5px] text-[hsl(var(--text-muted))] leading-relaxed">
            <Check className="inline w-3 h-3 mr-1 text-[hsl(var(--success))]" strokeWidth={2.5}/>
            Owner and Admin roles bypass all permission checks. Staff see only the sections they can read, and action buttons appear only where they can write.
          </div>
        </div>

        <div className="px-6 py-4 border-t border-[hsl(var(--border))] flex items-center justify-end gap-2 bg-[hsl(var(--background))]/50">
          <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
          <Button type="button" onClick={save} disabled={saving} data-testid="permissions-save-btn">
            {saving ? "Saving…" : "Save permissions"}
          </Button>
        </div>
      </div>
    </div>
  );
}
